import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import type { ModificationResult, UpdateRecord } from "../model";

export interface ActionItem {
  id: number;
  content: string;
  projectId?: number;
  status: "actionable" | "done" | "not actionable";
  date?: string;
  delegatedTo?: string;
}

export interface Project {
  id: number;
  name: string;
  desiredOutcome: string;
  actionItems?: ActionItem[];
}

const API_BASE = "/api";

const useAuthenticatedFetch = () => {
  const { getToken } = useAuth();

  return async (url: string, options: RequestInit = {}) => {
    const token = await getToken();

    const headers = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    return fetch(url, { ...options, headers });
  };
};

export const useProjects = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ["projects"],
    queryFn: async (): Promise<Project[]> => {
      const response = await authenticatedFetch(`${API_BASE}/projects`);
      if (!response.ok) throw new Error("Failed to fetch projects");
      return response.json();
    },
  });
};

export const useActionItemsInProject = (projectId: number) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ["actionItems", "project", projectId],
    queryFn: async (): Promise<ActionItem[]> => {
      const response = await authenticatedFetch(
        `${API_BASE}/projects/${projectId}/action-items`,
      );
      if (!response.ok) throw new Error("Failed to fetch project action items");
      return response.json();
    },
    enabled: !!projectId,
  });
};

export const useActionItemsWithoutProject = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ["actionItems", "global"],
    queryFn: async (): Promise<ActionItem[]> => {
      const response = await authenticatedFetch(
        `${API_BASE}/action-items/global`,
      );
      if (!response.ok) throw new Error("Failed to fetch global action items");
      return response.json();
    },
  });
};

export const useAllActionItems = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ["actionItems", "all"],
    queryFn: async (): Promise<ActionItem[]> => {
      const response = await authenticatedFetch(`${API_BASE}/action-items`);
      if (!response.ok) throw new Error("Failed to fetch all action items");
      return response.json();
    },
  });
};

interface ProcessCaptureResponse {
  success: boolean;
  transcript: string;
  modifications: ModificationResult;
}

export const useProcessCapture = (projectId?: number) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async (payload: {
      transcript: string;
      pendingUpdates?: UpdateRecord[];
      rejectedUpdates?: UpdateRecord[];
      acceptedUpdates?: UpdateRecord[];
    }): Promise<ProcessCaptureResponse> => {
      const endpoint = projectId
        ? `${API_BASE}/projects/${projectId}/process-capture`
        : `${API_BASE}/process-capture`;

      const response = await authenticatedFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to process capture");
      return response.json();
    },
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async (
      project: Omit<Project, "id" | "actionItems">,
    ): Promise<Project> => {
      const response = await authenticatedFetch(`${API_BASE}/projects`, {
        method: "POST",
        body: JSON.stringify(project),
      });
      if (!response.ok) throw new Error("Failed to create project");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      desiredOutcome,
    }: {
      id: number;
      name?: string;
      desiredOutcome?: string;
    }): Promise<Project> => {
      const response = await authenticatedFetch(`${API_BASE}/projects/${id}`, {
        method: "PUT",
        body: JSON.stringify({ name, desiredOutcome }),
      });
      if (!response.ok) throw new Error("Failed to update project");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const response = await authenticatedFetch(`${API_BASE}/projects/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete project");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["actionItems"] });
    },
  });
};

export const useCreateActionItem = () => {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async (
      actionItem: Omit<ActionItem, "id">,
    ): Promise<ActionItem> => {
      const url = actionItem.projectId
        ? `${API_BASE}/projects/${actionItem.projectId}/action-items`
        : `${API_BASE}/action-items/global`;

      // For project-specific endpoints, send all fields except id
      // For global endpoints, send the full actionItem object except id
      const body = actionItem.projectId
        ? {
            content: actionItem.content,
            status: actionItem.status,
            date: actionItem.date,
            delegatedTo: actionItem.delegatedTo,
          }
        : {
            content: actionItem.content,
            status: actionItem.status,
            date: actionItem.date,
            delegatedTo: actionItem.delegatedTo,
          };

      const response = await authenticatedFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error("Failed to create action item");
      return response.json();
    },
    onSuccess: (data) => {
      if (data.projectId) {
        queryClient.invalidateQueries({
          queryKey: ["actionItems", "project", data.projectId],
        });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["actionItems", "global"] });
      }
      queryClient.invalidateQueries({ queryKey: ["actionItems", "all"] });
    },
  });
};

export const useUpdateActionItem = () => {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<ActionItem> & { id: number }): Promise<ActionItem> => {
      const response = await authenticatedFetch(
        `${API_BASE}/action-items/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(updates),
        },
      );
      if (!response.ok) throw new Error("Failed to update action item");
      return response.json();
    },
    onSuccess: (data) => {
      if (data.projectId) {
        queryClient.invalidateQueries({
          queryKey: ["actionItems", "project", data.projectId],
        });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["actionItems", "global"] });
      }
      queryClient.invalidateQueries({ queryKey: ["actionItems", "all"] });
    },
  });
};

export const useDeleteActionItem = () => {
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: number;
      projectId?: number;
    }): Promise<void> => {
      const response = await authenticatedFetch(
        `${API_BASE}/action-items/${id}`,
        {
          method: "DELETE",
        },
      );
      if (!response.ok) throw new Error("Failed to delete action item");
    },
    onSuccess: (_: void, variables: { id: number; projectId?: number }) => {
      if (variables.projectId) {
        queryClient.invalidateQueries({
          queryKey: ["actionItems", "project", variables.projectId],
        });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["actionItems", "global"] });
      }
      queryClient.invalidateQueries({ queryKey: ["actionItems", "all"] });
    },
  });
};
